(() => {
  const desktop = matchMedia('(min-width: 1025px)');
  const bannerRequests = new Map();
  function syncBanners(panel) {
    panel.querySelectorAll('[data-kdm-collection-banner]').forEach(card => {
      if (card.dataset.bannerSynced) return;
      const url = new URL(card.href, location.origin);
      if (url.origin !== location.origin) return;
      // Collection templates can override the collection image in the theme editor.
      if (!bannerRequests.has(url.href)) {
        bannerRequests.set(url.href, fetch(url.href, {signal: AbortSignal.timeout(10000)})
          .then(response => response.ok ? response.text() : Promise.reject(new Error('Banner request failed')))
          .then(html => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const hero = doc.querySelector('.klaim-collection-campaign__hero-image');
            const src = hero?.getAttribute('src');
            if (!src) return null;
            return {src: new URL(src, url.href).href, srcset: hero.getAttribute('srcset'), alt: hero.getAttribute('alt')};
          })
          .catch(() => { bannerRequests.delete(url.href); return null; }));
      }
      bannerRequests.get(url.href).then(banner => {
        if (!banner) return;
        const image = card.querySelector('img');
        if (!image) return;
        image.removeAttribute('srcset');
        image.src = banner.src;
        if (banner.srcset) { image.srcset = banner.srcset; image.sizes = '22vw'; }
        if (banner.alt) image.alt = banner.alt;
        card.dataset.bannerSynced = 'true';
      });
    });
  }
  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const tabs = [...root.querySelectorAll('[data-kdm-tab]')];
    const panels = [...root.querySelectorAll('[data-kdm-panel]')];
    let closeTimer;
    let active = null;
    let clickedTab = null;
    const headerPanels = window.KlaimHeaderPanels;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const motions = new Map();
    const stopMotion = panel => {
      motions.get(panel)?.forEach(animation => animation.cancel());
      motions.delete(panel);
    };
    const hidePanel = (panel, immediate = false) => {
      stopMotion(panel);
      panel.inert = true;
      if (panel.hidden || immediate || reducedMotion.matches || !panel.animate) { panel.hidden = true; return; }
      const animation = panel.animate([
        { transform:'translateY(0)', opacity:1 },
        { transform:'translateY(-16px)', opacity:0 }
      ], { duration:200, easing:'cubic-bezier(.4,0,1,1)', fill:'forwards' });
      motions.set(panel, [animation]);
      animation.onfinish = () => { if (panel.inert) { panel.hidden = true; stopMotion(panel); } };
    };
    const position = () => {
      const header = root.closest('#SiteHeader');
      if (header) root.style.setProperty('--kdm-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    };
    const progress = panel => {
      const track = panel.querySelector('.kdm-track');
      if (!track || !track.scrollWidth) return;
      const ratio = Math.min(1, (track.scrollLeft + track.clientWidth) / track.scrollWidth);
      panel.style.setProperty('--kdm-progress', `${ratio * 100}%`);
    };
    const close = (focus = false) => {
      clearTimeout(closeTimer);
      const previous = active;
      active = null;
      clickedTab = null;
      tabs.forEach(tab => tab.setAttribute('aria-expanded', 'false'));
      panels.forEach(panel => hidePanel(panel, !desktop.matches));
      headerPanels?.deactivate('desktop-menu');
      if (focus && previous) previous.focus({preventScroll: true});
    };
    const open = tab => {
      if (!desktop.matches) return;
      clearTimeout(closeTimer);
      if (active === tab) return;
      headerPanels?.activate('desktop-menu', tab);
      active = tab;
      clickedTab = null;
      position();
      tabs.forEach(item => item.setAttribute('aria-expanded', String(item === tab)));
      panels.forEach(panel => {
        if (panel.dataset.kdmPanel !== tab.dataset.kdmTab) { hidePanel(panel, true); return; }
        stopMotion(panel);
        panel.hidden = false;
        panel.inert = false;
        progress(panel);
        syncBanners(panel);
        if (!reducedMotion.matches && panel.animate) {
          const animations = [panel.animate([
            {transform:'translateY(-24px)',opacity:0}, {transform:'translateY(0)',opacity:1}
          ], {duration:440,easing:'cubic-bezier(.22,1,.36,1)'})];
          [...panel.children].forEach((child, index) => {
            animations.push(child.animate([
              {transform:'translateY(-12px)',opacity:.6}, {transform:'translateY(0)',opacity:1}
            ], {duration:480,delay:Math.min(index,3)*40+60,easing:'cubic-bezier(.22,1,.36,1)',fill:'backwards'}));
          });
          motions.set(panel, animations);
        }
      });
    };
    tabs.forEach(tab => {
      tab.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && (!headerPanels?.active || headerPanels.active === 'desktop-menu')) open(tab); });
      tab.addEventListener('click', () => {
        // Hover can open before click; only a second explicit click should close.
        if (active === tab && clickedTab === tab) { close(); return; }
        open(tab);
        clickedTab = tab;
      });
      tab.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault(); open(tab);
          panels.find(panel => !panel.hidden)?.querySelector('a')?.focus();
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const next = tabs[(tabs.indexOf(tab) + 1) % tabs.length];
          next.focus(); open(next);
        }
      });
    });
    headerPanels?.register('desktop-menu', { element: root, close: () => close(true), modal: false });
    root.addEventListener('pointerenter', () => clearTimeout(closeTimer));
    root.addEventListener('pointerleave', () => { closeTimer = setTimeout(() => { if (!root.contains(document.activeElement)) close(); }, 160); });
    root.addEventListener('focusout', event => { if (!root.contains(event.relatedTarget)) closeTimer = setTimeout(() => close(), 160); });
    root.addEventListener('focusin', () => clearTimeout(closeTimer));
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && active) { event.preventDefault(); close(true); } });
    document.addEventListener('pointerdown', event => { if (!root.contains(event.target)) close(); });
    panels.forEach(panel => {
      const track = panel.querySelector('.kdm-track');
      track?.addEventListener('scroll', () => progress(panel), {passive: true});
      track?.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
        const canScroll = event.deltaY > 0 ? track.scrollLeft < track.scrollWidth - track.clientWidth - 1 : track.scrollLeft > 0;
        if (canScroll) { event.preventDefault(); track.scrollLeft += event.deltaY; }
      }, {passive: false});
      track?.addEventListener('keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        track.scrollBy({left: (event.key === 'ArrowRight' ? 1 : -1) * track.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      });
    });
    desktop.addEventListener('change', () => close());
    reducedMotion.addEventListener('change', () => {
      if (!reducedMotion.matches) return;
      panels.forEach(panel => { stopMotion(panel); if (panel.inert) panel.hidden = true; });
    });
    window.addEventListener('resize', () => { position(); panels.filter(panel => !panel.hidden).forEach(progress); }, {passive: true});
    window.addEventListener('scroll', () => { if (active) position(); }, {passive: true});
  }
  document.querySelectorAll('[data-klaim-desktop-menu]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-klaim-desktop-menu]').forEach(init));
})();
